import type { UnionToIntersection } from 'type-fest';

import type {
  THygieneTaskType,
  TTreatmentType,
} from '@/react-query/insights/types';
import type { AdjustmentVersion } from '@/react-query/useGetSharpenedImages';
import { adjustmentVersions } from '@/react-query/useGetSharpenedImages';

import {
  ConfidenceThresholdValues,
  binValues,
} from '../modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import type {
  BinValues,
  ConfidenceScoreSliderValues,
} from '../modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import type { TToothProcedureNames } from '../react-query/useVisitSummaries';

export type LocalizedCopy = { en?: string; fr?: string };

export type DevicePreferences = {
  scrollToCurrent: string;
};

export type InsightsDashboard =
  | 'CLINICAL'
  | 'HYGIENE'
  | 'ENGAGEMENT'
  | 'BENCHMARKS';

export type VoiceNotesPreferences =
  | { isVideaNotesEnabled?: boolean } // this is the main feature flag for Videa Notes
  | { enableQuickNotes?: boolean }
  | { enableQuickNotesPatientId?: boolean }
  | { enableVoiceNotesConsent?: boolean }
  | { enableVoiceNotesStandalone?: boolean }
  | {
      voiceNotesTemplatesConfig: {
        defaultTemplateId: string | null;
        hiddenTemplateIds: string[];
      };
    }
  | { gateNotesLocalAsyncUpload?: boolean }
  | { gateNotesSynapseAsyncUpload?: boolean }
  | { ambientNotesLanguageSupport?: string[] };

export type PreferenceUpdateBody =
  | VoiceNotesPreferences
  | { enableVideaReferral: boolean }
  | { oralinxReferralRequestVersion: number }
  | { fluorideTaskAgeLimit: number }
  | { useNewTreatmentGroups: boolean }
  | { adjustmentVersion: string }
  | { adjustmentVersionHD2: string }
  | { binThreshold?: BinValues }
  | { cdnEnabled: boolean }
  | { cleanClaims: boolean }
  | { confidenceThreshold: ConfidenceScoreSliderValues }
  | { crownIndicatorMinimumDmf?: number }
  | { crownOpportunity: boolean }
  | { DDAISharpeningEnabled: boolean }
  | { displayDentalPhotos?: boolean | 'disabled' | 'off' | 'on' }
  | { displayDentalThumbnails?: boolean }
  | { displayPRLSegmentations: boolean }
  | { displayRALSegmentations: boolean }
  | { dmfEnabled: boolean }
  | { enableAiRerun: boolean }
  | { allowRerunAnalysesFromBridgeLinkEvent: boolean }
  | { enableBatchVisitSummariesProcessing: boolean }
  | { enableDailyGoals: boolean }
  | { enableDistinctFilterRerunButton: boolean }
  | { enableDevDashboards: boolean }
  | { enableHqOnThumbnails: boolean }
  | { enableLatestImagesV2?: boolean }
  | { enableOrgWideSearch: boolean }
  | { enablePatientsV2: boolean }
  | { enableRecommendationInteractions: boolean }
  | { enableScanOpportunityTask?: boolean }
  | { enableScanOpportunityTask?: boolean }
  | { enableSeriesView: boolean }
  | { fmxSummaryEnabled: boolean }
  | { hasVaultPractice: boolean }
  | { historicalTriggerEnabled: boolean }
  | { hqOffByDefault: boolean }
  | { huddleDisabled: boolean }
  | { huddleVersion: 'v1' | 'v2' }
  | { incipientIndicatorEnabled: boolean }
  | { incipientOpportunity: boolean }
  | {
      indicatorCopy: {
        crown?: string;
        extraction?: string;
        filling?: string;
        implant?: string;
        incipient?: string;
        ortho?: string;
        rootCanal?: string;
        srp?: string;
        locSrp?: string;
      };
    }
  | { insSummaryModal: boolean }
  | { isInsuranceEligibilityEnabled: boolean }
  | { insuranceExportPreviewPages?: 'paginated' | 'single' }
  | { isPlanningModeDisabled?: boolean }
  | { onboardingPopupEnabled: boolean }
  | { preferDisplayOverSharpened?: boolean }
  | { preferDisplayOverSharpened?: boolean }
  | { restorativeOpportunity: boolean }
  | { rolloutAverageDmfImageViewer: boolean }
  | { sageTxRolloutFlag: boolean }
  | { segmentationEnabled: boolean }
  | { showPlannedTxInIndicator: boolean }
  | { srpIndicatorEnabled: boolean }
  | { rootCanalIndicatorEnabled: boolean }
  | { rootCanalOpportunity: boolean }
  | { extractionIndicatorEnabled: boolean }
  | { extractionOpportunity: boolean }
  | { fillingIndicatorEnabled: boolean }
  | { fillingOpportunity: boolean }
  | { alignerIndicatorEnabled: boolean }
  | { alignerOpportunity: boolean }
  | { implantCrownIndicatorEnabled: boolean }
  | { implantCrownOpportunity: boolean }
  | { locSrpIndicatorEnabled: boolean }
  | { locSrpOpportunity: boolean }
  | { srpOpportunity: boolean }
  | { standalonePollingInterval: number }
  | { taskBitewings: number }
  | { taskFluoride: number }
  | { taskFmx: number }
  | { taskNextAppointment: boolean }
  | { taskPerioChart: number }
  | { taskPerioMaintenance: number }
  | { taskProphy: number }
  | { taskScanOpportunity: number }
  | { toothLocalization?: 'fdi' | 'universal' }
  | { unscheduledScheduledToggleEnabled?: boolean }
  | {
      examplePreference: string;
    }
  | {
      srpClaimSummaryThresholds: {
        bwBoneLossThreshold?: number | undefined;
        paBoneLossThreshold?: number | undefined;
      };
    }
  | {
      enableInsights?: InsightsDashboard[] | false;
    }
  | { enableInsightsPOC?: boolean }
  | { narrativeDevToolEnabled: boolean }
  | { showSegmentationView?: boolean }
  | { saveSegmentationViewEnabled?: boolean }
  | { canOptInImageViewerRedesign?: boolean }
  | { enableCustomPracticeGroups?: boolean }
  | { narrativeDevToolEnabled: boolean }
  | { enableMultiSource?: boolean }
  | { showUnknownViz: boolean }
  | { spotlightProcedure: TToothProcedureNames | 'none' }
  | { newImageAnnotatorThumbnailsForClaims: boolean }
  | { localeSwitcherEnabled: boolean }
  | { enableInsightsPilotRegion: string }
  | { forceEnableRecommendations?: boolean }
  | { enableBenchmarksOnAllPages?: boolean }
  | { showQRCodeButton?: boolean }
  | { insightsEnabledRecos?: TTreatmentType[] }
  | { insightsEnabledTasks?: THygieneTaskType[] }
  | { disableAdjustmentForPanos?: boolean }
  | { claimAttachmentEnabled?: boolean }
  | { disablePracticePreferences?: boolean }
  | { isProviderNameDisabledInAttachment?: boolean }
  | { standaloneClaimsEnabled?: boolean }
  | { perioCarriersEnabled?: boolean }
  | { toothEnamelRepairCodeEnabled?: boolean }
  | {
      customPatientEducationTxCopy?: {
        filling?: LocalizedCopy;
        srp?: LocalizedCopy;
        crown?: LocalizedCopy;
        rct?: LocalizedCopy;
        tooth_remineralization?: LocalizedCopy;
        implant?: LocalizedCopy;
        perio_carriers?: LocalizedCopy;
        periodontal_maintenance?: LocalizedCopy;
        crown_pediatric?: LocalizedCopy;
        enamel_regen_pediatric?: LocalizedCopy;
        fluoride?: LocalizedCopy;
        spacer?: LocalizedCopy;
        sealant?: LocalizedCopy;
        aligner?: LocalizedCopy;
        bridge?: LocalizedCopy;
      };
    }
  | {
      allowEditingOrgLevelPreferences?: {
        customPatientEducationTxCopy?: boolean;
      };
    }
  | { showParentheticalTxNames?: boolean }
  | { isFeedbackWidgetEnabled?: boolean }
  | { enableClickBasedFindingInteraction?: boolean }
  | { enableInsightsEngagementMetric?: boolean }
  | { enableInsightsTableExport?: boolean }
  | { referralsPatientIdEnabled?: boolean }
  | { enableImageQualityAssessment?: boolean }
  | { enableClinicalInteractionsForDirectors?: boolean }
  | { enableSpecialistSearchRedesign?: boolean }
  | { enabledSegmentationsV2?: boolean }
  | { enableSharpnessFilterSelectionModal?: boolean }
  | { pulpotomyIndicatorEnabled?: boolean }
  | {
      pulpotomyOpportunity?: boolean;
    }
  | { showAIImpactModal?: boolean }
  | { enablePerioChartInClinicalAssist?: boolean }
  | { enableSharpeningHd2Selection?: boolean }
  | { imageQualityInsightsInImageViewer?: boolean }
  | { availableSharpeningFilters: AdjustmentVersion[] }
  | { enableDecodedGingivalMargin?: boolean }
  | { enableGrayPulpSegmentation?: boolean };

// Remember to add the preference to preferencesMetaData below if applicable!

type ExtractPreferenceKeys<T> = T extends { [key: string]: any }
  ? keyof T
  : never;

export type PreferenceKey = ExtractPreferenceKeys<PreferenceUpdateBody>;

// Type utility to extract the type of a specific preference
export type TPreferences = {
  [K in PreferenceKey]: Extract<PreferenceUpdateBody, { [P in K]: any }>[K];
};

interface PreferenceOption {
  key: PreferenceKey;
  type: readonly ('boolean' | 'string' | 'number')[];
  possibleValues: readonly (string | number)[];
}

// This `preferencesMetaData` constant should only contain preferences that are
// string (with fixed set of options), number (with fixed set of options),
// boolean and string, AND boolean and number.
export const preferencesMetaData: Partial<
  Record<PreferenceKey, PreferenceOption>
> = {
  binThreshold: {
    key: 'binThreshold',
    type: ['number'],
    possibleValues: binValues,
  },
  confidenceThreshold: {
    key: 'confidenceThreshold',
    type: ['number'],
    possibleValues: ConfidenceThresholdValues,
  },
  displayDentalPhotos: {
    key: 'displayDentalPhotos',
    type: ['boolean', 'string'],
    possibleValues: ['disabled', 'off', 'on'],
  },
  huddleVersion: {
    key: 'huddleVersion',
    type: ['string'],
    possibleValues: ['v1', 'v2'],
  },
  insuranceExportPreviewPages: {
    key: 'insuranceExportPreviewPages',
    type: ['string'],
    possibleValues: ['paginated', 'single'],
  },
  toothLocalization: {
    key: 'toothLocalization',
    type: ['string'],
    possibleValues: ['fdi', 'universal'],
  },
  adjustmentVersion: {
    key: 'adjustmentVersion',
    type: ['string'],
    possibleValues: adjustmentVersions,
  },
} as const;

export type PreferencesResponse = Partial<
  UnionToIntersection<PreferenceUpdateBody>
>;

export interface PreferenceHistoryUser {
  id: string;
  firstName: string;
  lastName: string;
}

export interface PreferenceHistoryResponse {
  value: string;
  updatedBy: string;
  updatedAt: string;
  source: 'practice' | 'organization' | 'user';
  user?: PreferenceHistoryUser;
}
